import type { SignUpFormValues } from '../../schemas';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import {
  Asterisk,
  FormControl,
  FormField,
  FormHelper,
  FormItem,
  FormLabel,
} from '@paid-ui/components/form';
import { Input } from '@paid-ui/components/input';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { BusinessStructure } from '@paid-ui/enums/business';

import { roleInBusinessOptions } from '../../constants/business';
import { signUpManager } from '../../models';
import { RoleInBusiness, SignUpStep } from '../../models/enums';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

const getRoleInBusinessOptions = (structure: BusinessStructure | '') => {
  switch (structure) {
    case BusinessStructure.PARTNERSHIP: {
      return roleInBusinessOptions.filter(({ value }) => value !== RoleInBusiness.SOLE_TRADER);
    }
    case BusinessStructure.SOLE_TRADER: {
      return roleInBusinessOptions.filter(({ value }) => value !== RoleInBusiness.PARTNERSHIP);
    }
    case BusinessStructure.OTHER: {
      // Owner option carries the PARTNERSHIP value (both PARTNERSHIP and
      // SOLE_TRADER options share the "I'm the business owner" label and
      // the formatter only cares whether the user picked EMPLOYEE).
      return roleInBusinessOptions.filter(({ value }) => value !== RoleInBusiness.SOLE_TRADER);
    }
    default: {
      return roleInBusinessOptions;
    }
  }
};

export const StepBusinessDetails: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext<SignUpFormValues>();
  const { currentStep, invitedOwnerSignUp } = useSnapshot(signUpManager);
  const structure = form.watch('structure');
  const businessName = form.watch('businessName');
  const roleInBusiness = form.watch('roleInBusiness');
  const isValid =
    Boolean(businessName && roleInBusiness) && !form.getFieldState('businessName').invalid;
  const filteredRoleInBusinessOptions = getRoleInBusinessOptions(structure);

  const handleNext = () => {
    form.setValue('isEmployee', roleInBusiness === RoleInBusiness.EMPLOYEE);
    goToStep?.(SignUpStep.USER_DETAILS);
  };

  useEffect(() => {
    if (invitedOwnerSignUp) {
      switch (structure) {
        case BusinessStructure.PARTNERSHIP: {
          form.setValue('roleInBusiness', RoleInBusiness.PARTNERSHIP);
          break;
        }
        case BusinessStructure.SOLE_TRADER: {
          form.setValue('roleInBusiness', RoleInBusiness.SOLE_TRADER);
          break;
        }
        default:
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedOwnerSignUp, structure]);

  if (currentStep !== SignUpStep.BUSINESS_DETAILS) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="businessName"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="My business name" disabled={disabled} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
      </div>
      <FormField
        name="roleInBusiness"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Your role <Asterisk />
            </FormLabel>
            <FormControl>
              <RadioGroup
                {...field}
                onValueChange={(value) =>
                  form.setValue('roleInBusiness', value as RoleInBusiness, {
                    shouldValidate: true,
                  })
                }
                disabled={disabled}
                className="space-y-3.5"
              >
                {filteredRoleInBusinessOptions.map((option) => (
                  <RadioGroupCard key={option.value} value={option.value}>
                    {option.label}
                  </RadioGroupCard>
                ))}
              </RadioGroup>
            </FormControl>
            <FormHelper />
          </FormItem>
        )}
      />
      <Button
        variant="solid"
        disabled={disabled || !isValid}
        className="h-[44px]"
        onClick={handleNext}
      >
        Continue
      </Button>
    </div>
  );
};
